import apiClient from '../api/client';
import { updateAuthHeader } from '../api/client';
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  ForgotPasswordRequest,
} from '../../types/api.types';

class AuthService {
  /**
   * Super Admin Login
   * POST /super-admin/login
   */
  async superAdminLogin(credentials: LoginRequest): Promise<string> {
    console.log('🔑 Starting Super Admin Login...', credentials.email);
    const response = await apiClient.post<LoginResponse>('/super-admin/login', credentials);
    
    if (response.data.success && response.data.data) {
      let token: string;
      
      // Handle different response formats from backend
      if (typeof response.data.data === 'string') {
        token = response.data.data;
      } else if (typeof response.data.data === 'object') {
        // Extract token from object (try different possible keys)
        token = response.data.data.token || 
                response.data.data.jwt_token || 
                response.data.data.authToken || 
                '';
      } else {
        token = '';
      }
      
      console.log('✅ Login successful - Token extracted:', {
        tokenType: typeof response.data.data,
        tokenLength: token.length,
        tokenPreview: token.substring(0, 20) + '...'
      });
      
      if (!token) {
        console.error('❌ Token extraction failed:', response.data.data);
        throw new Error('Failed to extract authentication token');
      }
      
      // ✅ CRITICAL: Ensure token is stored as STRING, not object
      const tokenString = String(token).trim();
      
      if (tokenString.startsWith('[object') || tokenString === '[object Object]') {
        console.error('❌ Token is still an object after extraction!', response.data.data);
        throw new Error('Token extraction failed - received object instead of string');
      }
      
      console.log('✅ Token validated as string:', {
        length: tokenString.length,
        startsWithBearer: tokenString.startsWith('Bearer'),
        preview: tokenString.substring(0, 30) + '...'
      });
      
      // Store authentication data - ensure it's stored correctly
      try {
        localStorage.setItem('authToken', tokenString);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userRole', 'superadmin');
        localStorage.setItem('userEmail', credentials.email);
        
        // Verify it was stored
        const storedToken = localStorage.getItem('authToken');
        if (!storedToken || storedToken !== tokenString) {
          console.error('❌ Token storage failed!');
          throw new Error('Failed to store authentication token');
        }
        
        // Update axios default header
        updateAuthHeader(tokenString);
        
        console.log('📦 LocalStorage set (Super Admin):', {
          hasToken: !!localStorage.getItem('authToken'),
          tokenLength: localStorage.getItem('authToken')?.length || 0,
          tokenMatches: storedToken === tokenString,
          isAuthenticated: localStorage.getItem('isAuthenticated'),
          userRole: localStorage.getItem('userRole')
        });
      } catch (error) {
        console.error('❌ Error storing token:', error);
        throw error;
      }
      
      return token;
    }
    
    throw new Error(response.data.message || 'Login failed');
  }

  /**
   * Admin Login (separate endpoint if exists)
   * POST /admin/login
   */
  async adminLogin(credentials: LoginRequest): Promise<string> {
    console.log('🔑 Starting Admin Login...', credentials.email);
    const response = await apiClient.post<LoginResponse>('/admin/login', credentials);
    
    if (response.data.success && response.data.data) {
      let token: string;
      
      // Handle different response formats from backend
      if (typeof response.data.data === 'string') {
        token = response.data.data;
      } else if (typeof response.data.data === 'object') {
        // Extract token from object (try different possible keys)
        token = response.data.data.token || 
                response.data.data.jwt_token || 
                response.data.data.authToken || 
                '';
      } else {
        token = '';
      }
      
      console.log('✅ Login successful - Token extracted:', {
        tokenType: typeof response.data.data,
        tokenLength: token.length,
        tokenPreview: token.substring(0, 20) + '...'
      });
      
      if (!token) {
        console.error('❌ Token extraction failed:', response.data.data);
        throw new Error('Failed to extract authentication token');
      }
      
      // ✅ CRITICAL: Ensure token is stored as STRING, not object
      const tokenString = String(token).trim();
      
      if (tokenString.startsWith('[object') || tokenString === '[object Object]') {
        console.error('❌ Token is still an object after extraction!', response.data.data);
        throw new Error('Token extraction failed - received object instead of string');
      }
      
      console.log('✅ Token validated as string:', {
        length: tokenString.length,
        startsWithBearer: tokenString.startsWith('Bearer'),
        preview: tokenString.substring(0, 30) + '...'
      });
      
      // Store authentication data - ensure it's stored correctly
      try {
        localStorage.setItem('authToken', tokenString);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userRole', 'admin');
        localStorage.setItem('userEmail', credentials.email);
        
        // Verify it was stored
        const storedToken = localStorage.getItem('authToken');
        if (!storedToken || storedToken !== tokenString) {
          console.error('❌ Token storage failed!');
          throw new Error('Failed to store authentication token');
        }
        
        // Update axios default header
        updateAuthHeader(tokenString);
        
        console.log('📦 LocalStorage set (Admin):', {
          hasToken: !!localStorage.getItem('authToken'),
          tokenLength: localStorage.getItem('authToken')?.length || 0,
          tokenMatches: storedToken === tokenString,
          isAuthenticated: localStorage.getItem('isAuthenticated'),
          userRole: localStorage.getItem('userRole')
        });
      } catch (error) {
        console.error('❌ Error storing token:', error);
        throw error;
      }
      
      return token;
    }
    
    throw new Error(response.data.message || 'Login failed');
  }

  /**
   * Super Admin Forgot Password
   * POST /super-admin/forgot-password
   */
  async superAdminForgotPassword(data: ForgotPasswordRequest): Promise<string> {
    const response = await apiClient.post<ApiResponse<null>>(
      '/super-admin/forgot-password',
      data
    );
    return response.data.message;
  }

  /**
   * Admin Forgot Password
   * POST /admin/forgot-password
   */
  async adminForgotPassword(data: ForgotPasswordRequest): Promise<string> {
    const response = await apiClient.post<ApiResponse<null>>(
      '/admin/forgot-password',
      data
    );
    return response.data.message;
  }

  /**
   * Forgot Password (Legacy - for backward compatibility)
   * POST /super-admin/forgot-password
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<string> {
    return this.superAdminForgotPassword(data);
  }

  /**
   * Logout - Clear all auth data
   */
  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userEmail');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return localStorage.getItem('isAuthenticated') === 'true' && !!localStorage.getItem('authToken');
  }

  /**
   * Get current user role
   */
  getUserRole(): 'admin' | 'superadmin' | null {
    return localStorage.getItem('userRole') as 'admin' | 'superadmin' | null;
  }

  /**
   * Get auth token
   */
  getToken(): string | null {
    return localStorage.getItem('authToken');
  }
}

export default new AuthService();
